import {
  Component,
  OnInit,
  OnDestroy,
  DoCheck,
  HostListener,
  HostBinding,
  ViewChild,
  ElementRef,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  effect,
  signal,
  NgZone,
} from '@angular/core';
import { BrandOfflineService } from '@omni/services/brand/brand.service';
import { buildSpeechPhrases, pickConfiguredPhrases } from '@omni/services/speech/speech-phrases';
import { IoConfigurationFeatureCodes } from '@omni/classes/authentication/user.class';
import { CommonModule } from '@angular/common';
import { IonicModule, ToastController, AlertController } from '@ionic/angular';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { DeviceService } from '../../services/device/device.service';
import { StatusBarAppearanceService } from '../../services/status-bar/status-bar-appearance.service';
import { AuthenticationService, SpeechConfig } from '../../services/authentication.service';
import { AndroidPermissions } from '@awesome-cordova-plugins/android-permissions/ngx';
import { MeetingSummaryFeature } from '../../ai/features/meeting-summary.feature';
import { AppointmentActivity } from '../../classes/activity/appointment.activity.class';
import { MeetingSummaryResult } from '../../models/nova-voice.model';
import { ActivityService } from '@omni/services/activity/activity.service';
import { DiskService } from '@omni/services/disk/disk.service';
import { IONote } from '@omni/classes/io/io-note.class';
import { NovaConversationalComponent } from './pages/conversational/conversational.component';
import { NovaCallAnalysisComponent, ApplySelections } from './pages/call-analysis/call-analysis.component';
import { NovaCoachingComponent } from './pages/coaching/coaching.component';
import { MeetingDataService } from '../../data-services/meeting/meeting.data.service';
import { EventsService } from '../../services/events/events.service';
import { FeatureActionsMap } from '../../classes/authentication/user.class';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import type { ConversationTranscriber } from 'microsoft-cognitiveservices-speech-sdk';
import { SpeechRecogniserService } from '@omni/services/speech/speech-recogniser.service';
import { NovaContext, NovaContextService, NovaRecordContext } from './nova-context.service';
import { AssistantChatFeature, AssistantChatMessage } from '@omni/ai/features/assistant-chat.feature';
import { NovaAssistantChatComponent } from './pages/assistant-chat/assistant-chat.component';
import { RecordInsightsFeature } from '@omni/ai/features/record-insights.feature';
import { RecordRef, RecordKind } from '@omni/ai/features/record-ref';
import { textCard, type CardResponse, type CardAction } from '@omni/ai/features/card.schema';
import { NotificationService, ToastStyle } from '@omni/services/notification/notification.service';
import type { TokenUsage } from '@omni/ai/transports/ai-transport';
import { AssistantFeedbackService } from '@omni/ai/services/assistant-feedback.service';
import type { NovaFeedbackEvent } from './nova-feedback-footer/nova-feedback-footer.component';
import { NovaRecordSummaryComponent } from './pages/record-summary/record-summary.component';
import { AssistantAttachment, ConversationOrigin } from '@omni/ai/features/assistant-attachment';
import { NovaConversationEntry } from '@omni/ai/services/nova-conversation-index.service';
import { NovaAttachPickerComponent } from './nova-attach-picker/nova-attach-picker.component';
import { NovaConversationListComponent } from './nova-conversation-list/nova-conversation-list.component';
import { NavigationTools } from '@omni/ai/tools/client-tools/navigation.tools';
import {
  SafeAreaInsets,
  readSafeAreaInsets,
} from '@omni/utility/safe-area-insets';
import { NOVA_HEADER_ACTION_WIDTH, NovaHeaderLayout, novaHeaderLayout } from './nova-header-layout';

/**
 * Which feature action licenses each record-summary kind. Record insights
 * (contact / account / spotlight) all ride on the general Ask-Nova assistant
 * capability, so they share the NOVA_ASSISTANT feature action.
 */
const RECORD_KIND_FEATURE_ACTION: Record<RecordKind, FeatureActionsMap> = {
  contact: FeatureActionsMap.NOVA_ASSISTANT,
  account: FeatureActionsMap.NOVA_ASSISTANT,
  spotlight: FeatureActionsMap.NOVA_ASSISTANT,
};

/**
 * Nova harness: mounted once at the app root so it is always available and owns
 * speech transcription end-to-end. It is context-driven — meeting scope is
 * supplied by `NovaContextService` (a page plugs it via `[novaMeetingContext]`),
 * never pushed by a parent. It renders a global FAB and a two-mode overlay
 * (`isOpen`): Assistant chat (default) and the meeting transcript/analysis flow
 * (only when a meeting is plugged).
 */

/** A header element whose width feeds the header layout. */
type HeaderPart = 'content' | 'tabs' | 'dropdown' | 'chip';

/** One row in the Insights ⌄ context switcher. `record` is null for the meeting. */
interface ContextChoice {
  id: string;
  label: string;
  tag: string;
  icon: string;
  current: boolean;
  record: RecordRef | null;
}
@Component({
  selector: 'nova-voice',
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    TranslateModule,
    NovaConversationalComponent,
    NovaCallAnalysisComponent,
    NovaCoachingComponent,
    NovaAssistantChatComponent,
    NovaRecordSummaryComponent,
    NovaAttachPickerComponent,
    NovaConversationListComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './nova-voice.component.html',
  styleUrls: ['./nova-voice.component.scss'],
})
export class NovaVoiceComponent implements OnInit, OnDestroy, DoCheck {
  @ViewChild('swiper') swiperRef: ElementRef;

  mode: 'assistant' | 'meeting' | 'record' = 'assistant';
  chatMessages: AssistantChatMessage[] = [];
  /**
   * Greeting + starter pills the Assistant agent composes for an empty thread.
   * Null until it resolves, so the empty state shows its static hint meanwhile
   * rather than a spinner on an already-idle screen.
   */
  welcomeCard: CardResponse | null = null;
  /** True while the welcome turn is in flight — the agent takes several seconds. */
  isWelcomeLoading = false;
  isSendingChat = false;

  // Record-summary mode (parallel to meeting): rendered when activeRecord is set
  // and the rep opens it from the beacon (or the FAB ready bubble).
  recordSummary: CardResponse | null = null;
  /** Feedback-correlation metadata for `recordSummary`'s turn — null until an analyze() lands. */
  recordSummaryResponseId: string | null = null;
  recordSummaryUsage: TokenUsage | null = null;
  /** Feedback correlation for the meeting analysis. Null on an analysis restored from cache. */
  meetingSummaryResponseId: string | null = null;
  meetingSummaryUsage: TokenUsage | null = null;
  recordLastAnalyzedAt: Date | null = null;
  recordFlowState: 'pre' | 'processing' | 'analysis' = 'pre';
  /** True while the harness is fetching prior chat history (open / restore). */
  isLoadingChat = false;

  // Attach picker + conversation history
  assistantAttachments: AssistantAttachment[] = [];
  isPickerOpen = false;
  isHistoryOpen = false;
  /**
   * Whether any past conversation exists. The history button is hidden until there is something to
   * show, so a first-time rep is not offered an empty sheet.
   */
  hasConversationHistory = false;
  /** Insights ⌄ context-switcher menu (only meaningful with 2+ active contexts). */
  isContextMenuOpen = false;
  /** Menu of the view dropdown, which replaces the two tabs when the header is too narrow for them. */
  isViewMenuOpen = false;
  /** Menu of the secondary header actions, used when the header is too narrow for their buttons. */
  isMoreMenuOpen = false;

  /** Measured header widths, in pixels, that decide {@link headerLayout}. */
  readonly #headerWidths = signal({ content: 0, gap: 0, tabs: 0, dropdown: 0, chip: 0 });
  /** The header elements whose sizes are watched, by the width each one supplies. */
  readonly #headerParts = new Map<HeaderPart, HTMLElement>();
  #headerObserver: ResizeObserver | null = null;

  @ViewChild('header') set headerElement(ref: ElementRef<HTMLElement> | undefined) { this.#watchHeaderPart('content', ref); }
  @ViewChild('tabsSizer') set tabsSizer(ref: ElementRef<HTMLElement> | undefined) { this.#watchHeaderPart('tabs', ref); }
  @ViewChild('dropdownSizer') set dropdownSizer(ref: ElementRef<HTMLElement> | undefined) { this.#watchHeaderPart('dropdown', ref); }
  @ViewChild('recordingChip') set recordingChip(ref: ElementRef<HTMLElement> | undefined) { this.#watchHeaderPart('chip', ref); }

  // Theme: dark by default, persisted in localStorage and reflected via host attr.
  isDark = true;

  // Desktop-only: expand the docked panel to fill the viewport. Reflected as a
  // host class so the overlay padding + container sizing can react in CSS.
  isFullscreen = false;

  @HostBinding('attr.data-theme') get themeAttr() { return this.isDark ? 'dark' : 'light'; }
  @HostBinding('class.nova-fullscreen') get fullscreenClass() { return this.isFullscreen; }

  isOpen: boolean = false;
  /** True briefly while the panel plays its exit animation before unmounting. */
  isClosing: boolean = false;
  currentPage: number = 0;
  analysisData: MeetingSummaryResult | null = null;
  isDesktop: boolean = false;
  isProcessing: boolean = false;
  capturedTranscript: string = '';
  /** Translation keys of the meeting analysis pages, in page order. */
  readonly pageLabelKeys = ['NOVA_PAGE_OVERVIEW', 'NOVA_PAGE_ANALYSIS', 'NOVA_PAGE_COACHING'];

  /**
   * Explicit meeting-flow state machine, maintained alongside `#isRecording`:
   *  - 'resolving'  — opening the meeting; checking for a previously-saved summary.
   *  - 'pre'        — meeting overlay open, not yet transcribing (start panel).
   *  - 'live'       — recording in progress (live transcript view / collapsed FAB).
   *  - 'processing' — transcript captured, analysis running.
   *  - 'analysis'   — analysisData exists; the page-nav + pages are shown.
   */
  meetingFlowState: 'resolving' | 'pre' | 'live' | 'processing' | 'analysis' = 'pre';

  /** Inline message shown in the `pre` view when transcription fails to start. */
  meetingStartError: string = '';

  // Draggable docked panel (desktop): null = default docked position (bottom-right).
  panelLeft: number | null = null;
  panelTop: number | null = null;
  #panelDragging = false;
  #panelDragStartX = 0;
  #panelDragStartY = 0;
  #panelStartLeft = 0;
  #panelStartTop = 0;

  // Floating action button (self-contained Nova trigger on the meeting detail pane).
  fabTop: number = 0;
  fabLeft: number = 0;
  recordingElapsed: string = '00:00';
  #dragging = false;
  #dragMoved = false;
  #dragStartY = 0;
  #dragStartTop = 0;
  #dragStartX = 0;
  #dragStartLeft = 0;
  /** Safe-area insets read when the current drag started. */
  #dragInsets: SafeAreaInsets = { top: 0, right: 0, bottom: 0, left: 0 };
  /** Distance from the button's left edge to the right edge of the safe area, kept across resizes and rotations. */
  #fabRightOffset = 72;
  #recordStartMs = 0;
  #recordTimer: any = null;

  readonly #meetingSummary = inject(MeetingSummaryFeature);

  readonly #brandService = inject(BrandOfflineService);
  readonly #recordInsights = inject(RecordInsightsFeature);
  readonly #toastCtrl = inject(ToastController);
  readonly #alertCtrl = inject(AlertController);
  readonly #deviceService = inject(DeviceService);
  readonly #statusBarAppearance = inject(StatusBarAppearanceService);
  /** Whether the system bar icons are currently light because the dark panel covers the bars. */
  #lightSystemBarIcons = false;
  readonly #meetingDataService = inject(MeetingDataService);
  readonly #activityService = inject(ActivityService);
  readonly #disk = inject(DiskService);
  readonly #events = inject(EventsService);
  readonly #authService = inject(AuthenticationService);
  readonly #androidPermission = inject(AndroidPermissions);
  readonly #context = inject(NovaContextService);
  readonly #assistant = inject(AssistantChatFeature);
  readonly #navTools = inject(NavigationTools);
  readonly #feedback = inject(AssistantFeedbackService);
  readonly #zone = inject(NgZone);
  readonly #recognisers = inject(SpeechRecogniserService);
  readonly #translate = inject(TranslateService);
  readonly #notifications = inject(NotificationService);
  readonly #destroy$ = new Subject<void>();

  /** True while the device has no connection; drives the offline label and the disabled composer. */
  isOffline = false;

  get activity() { return this.#context.activeMeeting(); }
  get hcpName(): string {
    const contact = this.activity?.contacts?.[0] as any;
    return contact?.fullName || contact?.fullname || '';
  }
  get meetingAvailable(): boolean {
    return !!this.activity
      && this.#meetingSummary.isAvailable()
      && this.#authService.hasFeatureAction(FeatureActionsMap.NOVA_TRANSCRIPT_ANALYSIS);
  }
  /**
   * General Ask-Nova chat is available when the rep has the assistant FA AND
   * the assistant agent is wired (Dataverse). A transcript-only user has the
   * meeting flow but no Assistant tab + no FAB outside of meetings.
   */
  get assistantAvailable(): boolean {
    return this.#authService.hasFeatureAction(FeatureActionsMap.NOVA_ASSISTANT)
      && this.#assistant.isAvailable();
  }
  /** Which record the rep explicitly chose via the Insights ⌄ switcher. Null =
   *  follow the most-recent context (the default). Sticky until that record
   *  leaves scope or the rep picks another. Records only — the meeting surface
   *  stays driven by the live recording, never by this selection. */
  readonly #selectedRecordKey = signal<string | null>(null);

  #recordKey(r: RecordRef): string { return `${r.kind}:${r.id}`; }

  /** All record contexts currently in scope, most-recent first. */
  get activeRecordContexts(): RecordRef[] {
    return this.#context.activeContexts()
      .filter((c): c is NovaRecordContext => c.kind === 'record')
      .map(c => c.record);
  }

  /** The record whose Insights are shown: the rep's explicit pick if it's still
   *  in scope, otherwise the most-recent record context. */
  get record(): RecordRef | null {
    const records = this.activeRecordContexts;
    const sel = this.#selectedRecordKey();
    if (sel) {
      const match = records.find(r => this.#recordKey(r) === sel);
      if (match) return match;
    }
    return records[0] ?? null;
  }
  get recordAvailable(): boolean {
    const r = this.record;
    return !!r
      && this.#recordInsights.isAvailable(r.kind)
      && this.#authService.hasFeatureAction(RECORD_KIND_FEATURE_ACTION[r.kind]);
  }

  /**
   * Number of distinct Nova surfaces the rep can switch between right now. The
   * header beacon (mode dropdown) is only meaningful when this is 2+ — with a
   * single surface there is nothing to switch to.
   */
  get availableModeCount(): number {
    return (this.assistantAvailable ? 1 : 0)
      + (this.meetingAvailable ? 1 : 0)
      + (this.recordAvailable ? 1 : 0);
  }

  // ── Header segment ([ Ask-Nova | Insights/Meeting ]) ────────────────────────
  // An artifact (meeting/record) in scope + assistant licensed → show a 2-tab
  // segment that REPLACES the wordmark: left = Ask-Nova (stable home), right =
  // the context-named artifact. No artifact (global FAB) → no segment, just the
  // wordmark. Transcript-only reps (no assistant FA) never see the segment, so
  // Discuss stays assistant-gated.
  get showSegment(): boolean {
    return this.assistantAvailable && (this.meetingAvailable || this.recordAvailable);
  }

  /** The right tab is always the insights view of whatever artifact is in scope
   *  (meeting analysis, account/contact insight, spotlight) — one label, not per-kind. */
  get artifactTabLabel(): string {
    return this.#translate.instant('NOVA_INSIGHTS');
  }

  /**
   * Left tab (Ask-Nova) when an artifact is in scope → the artifact-attached
   * discussion (find-or-draft, pre-attached), NOT a blank general chat. Within
   * an artifact's context "Ask-Nova" means "ask about this"; the New (✎) button
   * clears the attachment for a general thread. This tab is the single Discuss
   * entry point.
   */
  selectAssistantTab(): void {
    if (this.meetingAvailable) { void this.discussMeeting(); return; }
    if (this.recordAvailable) { void this.discussRecord(); return; }
    this.setMode('assistant');
  }

  /** Right tab (Insights) → the artifact surface. With a single context it opens
   *  that artifact; with several, the chevron menu lets the rep pick which one. */
  selectArtifactTab(): void {
    if (this.meetingAvailable) { void this.openMeeting(); return; }
    if (this.recordAvailable) { void this.openRecord(); }
  }

  // ── Insights ⌄ context switcher (only when 2+ contexts are in scope) ─────────

  /** True when more than one context is plugged, so the Insights tab gets a ⌄. */
  get hasMultipleContexts(): boolean {
    return this.#context.activeContexts().length > 1;
  }

  #kindTag(kind: RecordKind): string {
    const key = kind === 'account' ? 'ACCOUNT' : kind === 'spotlight' ? 'CONTACT_SPOTLIGHT' : 'CONTACT_SINGULAR';
    return this.#translate.instant(key);
  }
  #kindIcon(kind: RecordKind): string {
    return kind === 'account' ? 'business-outline' : kind === 'spotlight' ? 'compass-outline' : 'person-circle-outline';
  }

  /** Normalized list of in-scope contexts for the switcher menu (most-recent first). */
  get contextChoices(): ContextChoice[] {
    const shown = this.record;
    return this.#context.activeContexts().map(c => {
      if (c.kind === 'meeting') {
        return {
          id: `m:${c.activity.ID}`,
          label: c.activity.subject || this.hcpName || this.#translate.instant('MEETING'),
          tag: this.#translate.instant('MEETING'),
          icon: 'sparkles-outline',
          current: this.mode === 'meeting',
          record: null,
        };
      }
      const r = c.record;
      return {
        id: `r:${this.#recordKey(r)}`,
        label: r.label,
        tag: this.#kindTag(r.kind),
        icon: this.#kindIcon(r.kind),
        current: this.mode === 'record' && !!shown && this.#recordKey(shown) === this.#recordKey(r),
        record: r,
      };
    });
  }

  toggleContextMenu(): void { this.isContextMenuOpen = !this.isContextMenuOpen; }

  /** Stable identity for the switcher *ngFor so rows aren't destroyed/recreated
   *  every CD cycle (which detaches the tapped row mid-click). */
  trackContextChoice(_index: number, c: ContextChoice): string { return c.id; }

  /** Insights tab tap: switch to the artifact surface; if already there and there
   *  are several contexts, the same tap opens the switcher (the ⌄ affordance). */
  onArtifactTabClick(): void {
    if (this.hasMultipleContexts && this.mode !== 'assistant') {
      this.toggleContextMenu();
    } else {
      this.selectArtifactTab();
    }
  }

  /** Pick a context from the switcher: a record becomes the selected Insights
   *  record; the meeting just opens the meeting surface. */
  selectContextChoice(choice: ContextChoice): void {
    this.isContextMenuOpen = false;
    if (!choice.record) { void this.openMeeting(); return; }
    this.#selectedRecordKey.set(this.#recordKey(choice.record));
    void this.openRecord();
  }

  /** The header layout that fits the panel's current width; see {@link novaHeaderLayout}. */
  get headerLayout(): NovaHeaderLayout {
    if (!this.showSegment) return 'tabs';
    const { content, gap, tabs, dropdown, chip } = this.#headerWidths();
    const actions = this.#headerActionCount() * NOVA_HEADER_ACTION_WIDTH + chip;
    return novaHeaderLayout({ content, tabs, dropdown, actions, gap });
  }

  /** Label of the view dropdown: the view on screen. */
  get currentViewLabel(): string {
    return this.mode === 'assistant' ? 'Ask-Nova' : this.artifactTabLabel;
  }

  /** Pick a view from the view dropdown: Ask-Nova when `choice` is null, else that context's Insights. */
  selectView(choice: ContextChoice | null): void {
    this.isViewMenuOpen = false;
    if (choice) this.selectContextChoice(choice);
    else this.selectAssistantTab();
  }

  /**
   * Number of icon buttons in the header of the Ask-Nova view, which has the most, when each action
   * has its own button. Counting them in every view keeps the layout the same when the rep switches
   * between Ask-Nova and Insights.
   */
  #headerActionCount(): number {
    return 3 + (this.hasConversationHistory ? 1 : 0) + (this.isDesktop ? 1 : 0);
  }

  /** Watch a header element's size, or stop watching it when it leaves the view. */
  #watchHeaderPart(part: HeaderPart, ref: ElementRef<HTMLElement> | undefined): void {
    const previous = this.#headerParts.get(part);
    const next = ref?.nativeElement;
    if (previous === next) return;
    if (previous) this.#headerObserver?.unobserve(previous);
    if (!next) { this.#headerParts.delete(part); return; }
    this.#headerParts.set(part, next);
    this.#headerObserver ??= new ResizeObserver(() => this.#zone.run(() => this.#measureHeader()));
    this.#headerObserver.observe(next);
  }

  #measureHeader(): void {
    const width = (part: HeaderPart) => {
      const element = this.#headerParts.get(part);
      if (!element) return 0;
      const { marginLeft, marginRight } = getComputedStyle(element);
      return element.getBoundingClientRect().width + parseFloat(marginLeft) + parseFloat(marginRight);
    };
    const header = this.#headerParts.get('content');
    const style = header ? getComputedStyle(header) : null;
    this.#headerWidths.set({
      content: header && style ? header.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight) : 0,
      gap: style ? parseFloat(style.columnGap) || 0 : 0,
      tabs: width('tabs'),
      dropdown: width('dropdown'),
      chip: width('chip'),
    });
  }

  /** Id of the meeting the per-meeting UI state currently belongs to. */
  #lastMeetingId: string | null = null;

  // React when the active meeting *identity* changes (switched or left). On any
  // change we drop the previous meeting's cached analysis/transcript so it can't
  // bleed into the next one; on leaving scope mid-recording we also stop and fall
  // back to Assistant.
  readonly #scopeEffect = effect(() => {
    const id = this.#context.activeMeeting()?.ID ?? null;
    if (id === this.#lastMeetingId) return;
    this.#lastMeetingId = id;

    // The auto-attached composer chip belongs to the scope that seeded it.
    this.#pruneStaleAutoAttachments();

    // Capture synchronously — read by the async continuation below.
    const wasRecording = this.#isRecording;
    const wasInMeetingMode = this.mode === 'meeting';

    // Stale per-meeting state must not survive a meeting change (FAB 'ready'
    // dot, reopened analysis, etc. were showing the previous meeting's data).
    this.analysisData = null;
    this.meetingSummaryResponseId = null;
    this.meetingSummaryUsage = null;
    this.capturedTranscript = '';

    const meeting = this.#context.activeMeeting();

    // Proactively restore the moment the meeting is plugged — so the FAB shows
    // the "ready" bubble for an already-analyzed meeting without opening the panel.
    if (id && meeting && !wasRecording && this.meetingAvailable) {
      this.#meetingSummary.startConversation(meeting)
        .then(r => { if (this.#lastMeetingId === id) this.analysisData = r ?? null; })
        .catch(() => { /* no bubble — not fatal */ });
    }

    if (wasRecording || (!id && wasInMeetingMode)) {
      void (async () => {
        if (wasRecording) {
          await this.stopTranscription(); // stops the recognizer + timer
          // Recording was tied to the meeting we just left — collapse to a clean
          // state and let the rep know it stopped.
          this.capturedTranscript = '';
          this.meetingFlowState = 'pre';
          this.isOpen = false;
          this.#showToast(this.#translate.instant('NOVA_TRANSCRIPTION_STOPPED_LEFT_MEETING'), 'warning', 3000);
        }
        // Only bounce to Assistant when there is no meeting in scope at all —
        // and only if the rep actually has the Assistant FA. Otherwise leave
        // mode where it was (the FAB will hide on its own via showFab).
        if (!id && wasInMeetingMode && this.assistantAvailable) this.mode = 'assistant';
      })();
    }
  });

  /** Id of the record the per-record UI state currently belongs to. */
  #lastRecordKey: string | null = null;

  // React when the active record identity changes (host page mounted/unmounted, or
  // user navigated to a different contact/account). Drop the prior summary and
  // proactively restore from cache so the FAB can show its "ready" bubble.
  readonly #recordScopeEffect = effect(() => {
    // Follows the SELECTED record (Insights ⌄), which defaults to the most-recent
    // context — so switching contexts reloads that record's summary.
    const r = this.record;
    const key = r ? `${r.kind}_${r.id}` : null;
    if (key === this.#lastRecordKey) return;
    this.#lastRecordKey = key;

    // The auto-attached composer chip belongs to the scope that seeded it.
    this.#pruneStaleAutoAttachments();

    this.recordSummary = null;
    this.recordSummaryResponseId = null;
    this.recordSummaryUsage = null;
    this.recordLastAnalyzedAt = null;
    this.recordFlowState = 'pre';

    if (r && this.recordAvailable) {
      this.#recordInsights.startConversation(r)
        .then(cached => {
          if (this.#lastRecordKey !== key) return; // scope flipped while resolving
          if (cached) {
            this.recordSummary = cached;
            this.recordLastAnalyzedAt = new Date();
            this.recordFlowState = 'analysis';
          }
        })
        .catch(() => { /* no bubble — not fatal */ });
    }
  });

  // Speech-to-text state (recording is owned by this harness).
  #transcriber: ConversationTranscriber = null;
  #iosSpeech: any = null;
  #isRecording = false;
  #transcript = '';

  ngOnInit() {
    this.isDesktop = this.#deviceService.deviceFlags.desktop;
    this.#deviceService.isOfflineObservable
      .pipe(takeUntil(this.#destroy$))
      .subscribe(offline => this.#zone.run(() => this.isOffline = offline));
    const insets = readSafeAreaInsets();
    this.#placeFab(
      Math.round((window.innerHeight || 800) * 0.62),
      Math.round((window.innerWidth || 400) - insets.right - 72),
      insets,
    );

    // Restore persisted theme; default to dark when unset or anything but 'light'.
    this.isDark = localStorage.getItem('nova-theme') !== 'light';

    this.#events.observe('nova:meeting-started').pipe(takeUntil(this.#destroy$)).subscribe(async () => {
      if (!this.meetingAvailable) return;
      // Establish the meeting in the feature (this is the launch for the
      // footer/presentation-driven flow, which doesn't go through openMeeting) so
      // a later analyze() has the meeting + any existing conversation in scope.
      await this.#meetingSummary.startConversation(this.activity);
      await this.startTranscription();
      if (this.#isRecording) this.meetingFlowState = 'live';
    });
    this.#events.observe('nova:meeting-stopped').pipe(takeUntil(this.#destroy$)).subscribe(async () => {
      this.meetingFlowState = 'processing';
      const transcript = await this.stopTranscription();
      if (transcript) {
        await this.showAnalysis(); // settles meetingFlowState to 'analysis' or 'pre'
      } else {
        this.meetingFlowState = 'pre';
      }
    });

    // A meeting created from a voice note: analyze its transcript automatically so
    // the rep gets insights without opening the meeting or re-recording.
    this.#events.observe<{ meeting: AppointmentActivity; transcript: string }>('nova:analyze-note')
      .pipe(takeUntil(this.#destroy$))
      .subscribe(({ meeting, transcript }) => this.#analyzeNoteTranscript(meeting, transcript));
  }

  /**
   * Auto-analysis for a meeting created from a voice note (published on
   * `nova:analyze-note`). Plugs the meeting into Nova without opening its detail
   * pane, drives the FAB processing → ready, and leaves the summary ready to view.
   * Fire-and-forget from the caller; all gating lives here.
   */
  async #analyzeNoteTranscript(meeting: AppointmentActivity, transcript: string): Promise<void> {
    if (!meeting || !transcript?.trim()) return;
    if (this.#deviceService.isOffline) return; // analyze() needs Foundry
    // meetingAvailable needs an active meeting (chicken/egg), so check the two
    // independent gates directly before plugging. Meeting analysis is gated by
    // NOVA_TRANSCRIPT_ANALYSIS, not NOVA_ASSISTANT.
    if (!this.#meetingSummary.isAvailable()) return;
    if (!this.#authService.hasFeatureAction(FeatureActionsMap.NOVA_TRANSCRIPT_ANALYSIS)) return;
    // Don't stomp a live recording or an analysis already in flight.
    if (this.#isRecording || this.isProcessing) return;

    // Transient plug: makes meeting mode available and shows the FAB while we
    // analyze. The meeting pane that opens right after owns the *durable* context
    // (its [novaMeetingContext] directive), so we release ours in `finally` —
    // leaving that pane then clears Nova via the scope effect. Holding it here
    // would keep a second meeting context on the stack that the pane's unplug
    // never removes, so the FAB's insights badge would linger after the rep
    // navigates away. analyze() caches its result, so if our release momentarily
    // empties the stack the pane's startConversation restores the bubble.
    const ctx: NovaContext = { kind: 'meeting', activity: meeting };
    this.#context.plug(ctx);
    try {
      // Establish the meeting in the feature (required precondition for analyze()).
      await this.#meetingSummary.startConversation(meeting);
      this.mode = 'meeting';
      this.meetingFlowState = 'processing';
      this.isProcessing = true; // FAB → rotating 'processing'

      const summary = await this.#meetingSummary.analyze(transcript);
      // A newer context may have plugged while the network call was in flight.
      if (this.#context.activeMeeting()?.ID !== meeting.ID) return;
      if (summary) {
        this.analysisData = summary; // FAB → 'ready'; the rep taps to view the insights
        this.#captureMeetingTurn();
        this.meetingFlowState = 'analysis';
      } else {
        this.meetingFlowState = 'pre';
      }
    } catch (e) {
      console.error('Nova note-transcript analysis failed:', e);
      this.meetingFlowState = 'pre';
    } finally {
      this.isProcessing = false;
      this.#context.unplug(ctx);
    }
  }

  ngOnDestroy() {
    this.#headerObserver?.disconnect();
    this.#stopTimer();
    this.#destroy$.next();
    this.#destroy$.complete();
    if (this.#lightSystemBarIcons) {
      this.#statusBarAppearance.setLightIcons(false);
    }
  }

  /** Shows light system bar icons while the open panel covers the bars in the dark theme, and the app's dark icons otherwise. */
  ngDoCheck() {
    const coversBarsInDarkTheme =
      this.isOpen && this.isDark && (!this.isDesktop || this.isFullscreen);
    if (coversBarsInDarkTheme !== this.#lightSystemBarIcons) {
      this.#lightSystemBarIcons = coversBarsInDarkTheme;
      this.#statusBarAppearance.setLightIcons(coversBarsInDarkTheme);
    }
  }

  /** Begins live speech-to-text for the meeting (driven by the meeting-start event). */
  async startTranscription() {
    if (this.#deviceService.isOffline || this.#isRecording) return;
    try {
      await this.#startRecognizer();
      if (this.#isRecording) this.#startTimer();
    } catch (error) {
      console.error('Nova startTranscription error:', error);
    }
  }

  /** Stops live transcription and returns the captured transcript (no UI shown yet). */
  async stopTranscription(): Promise<string> {
    await this.#stopRecognizer();
    this.#stopTimer();
    this.capturedTranscript = this.#transcript.trim();
    return this.capturedTranscript;
  }

  async #startRecognizer() {
    const cfg = this.#authService.speechConfig;
    if (!cfg || !cfg.speechSubscriptionKey) return;
    const recogniser = await this.#recognisers.choose();
    if (!recogniser) {
      this.meetingStartError = this.#translate.instant('DICTATE_LANGUAGE_UNAVAILABLE');
      return;
    }
    this.#transcript = '';

    if (this.#deviceService.deviceFlags.ios) {
      if (!this.#iosSpeech) {
        this.#iosSpeech = (window as any).cordova?.plugins?.msSpeechService;
      }
      this.#iosSpeech.setupSpeechRecognizer(
        () => {
          this.#isRecording = true;
          this.#iosSpeech.startContinuousRecognition(
            (res: any) => this.#onIosResult(res),
            (err: any) => console.log(err),
          );
        },
        (err: any) => console.error('Nova init recognizer error:', err),
        recogniser.subscriptionKey, recogniser.region, recogniser.endpointId ?? '',
      );
      return;
    }

    if (this.#deviceService.isNativeApp && this.#deviceService.deviceFlags.android) {
      await this.#androidPermission.requestPermission(this.#androidPermission.PERMISSION.RECORD_AUDIO);
    }

    const { AudioConfig, ConversationTranscriber, PhraseListGrammar } = await import('microsoft-cognitiveservices-speech-sdk');
    const speechConfig = await this.#buildSpeechConfig(cfg);
    if (!speechConfig) return;
    if (recogniser.endpointId) speechConfig.endpointId = recogniser.endpointId;
    speechConfig.speechRecognitionLanguage = recogniser.language;

    // ConversationTranscriber gives real-time speaker diarization (who said what).
    this.#transcriber = new ConversationTranscriber(speechConfig, AudioConfig.fromDefaultMicrophoneInput());
    // Bias toward the rep's own products, so brand and molecule names survive into the
    // transcript the analysis reads. Best effort — never cost the rep their recording.
    const phrases = buildSpeechPhrases({
      configured: pickConfiguredPhrases(
        this.#authService.getIOConfigurationValue(IoConfigurationFeatureCodes.COMPANION_DICTATION_PHRASES_BU),
        this.#authService.getIOConfigurationValue(IoConfigurationFeatureCodes.COMPANION_DICTATION_PHRASES),
      ),
      brands: this.#brandService.brands,
    });
    if (phrases.length) {
      try {
        PhraseListGrammar.fromRecognizer(this.#transcriber).addPhrases(phrases);
      } catch (e) {
        console.warn('Speech phrase list not applied:', e);
      }
    }
    this.#transcriber.transcribed = (_s, e) => this.#onTranscribed(e);
    this.#transcriber.canceled = (_s: any, e: any) => {
      this.#recognisers.reportCancelled(recogniser);
      console.error('Nova transcription canceled:', e?.errorDetails);
    };
    this.#isRecording = true;
    this.#transcriber.startTranscribingAsync(() => {}, (err) => console.error(err));
  }

  /**
   * iOS final result. The Cordova callback runs outside Angular's zone, so the
   * append must re-enter via NgZone for the live transcript view to update.
   */
  #onIosResult(res: any) {
    if (!res?.isFinal) return;
    this.#zone.run(() => {
      this.#transcript += this.#formatSpeech(res.value, this.#transcript.length > 0);
    });
  }

  /**
   * Desktop `transcribed` segment. The SDK fires outside Angular's zone, so the
   * append must re-enter via NgZone for the live transcript view to re-render.
   */
  #onTranscribed(e: any) {
    const text = e?.result?.text?.trim();
    if (!text) return;
    const speaker = e.result.speakerId ? `${e.result.speakerId}: ` : '';
    this.#zone.run(() => {
      this.#transcript += (this.#transcript ? '\n' : '') + speaker + text;
    });
  }

  /**
   * Builds the SpeechConfig. Prefers Microsoft Entra token auth when a Speech
   * resource id is configured (no key shipped to the client); otherwise falls
   * back to subscription-key auth.
   */
  async #buildSpeechConfig(cfg: SpeechConfig) {
    const {SpeechConfig} = await import('microsoft-cognitiveservices-speech-sdk');
    if (cfg.speechSubscriptionKey) {
      return SpeechConfig.fromSubscription(cfg.speechSubscriptionKey, cfg.speechRegion);
    }
    return null;
  }

  async #stopRecognizer() {
    if (this.#deviceService.deviceFlags.ios) {
      this.#iosSpeech?.stopContinuousRecognition(
        () => (this.#isRecording = false),
        () => (this.#isRecording = false),
      );
      return;
    }
    if (this.#transcriber) {
      this.#transcriber.stopTranscribingAsync();
      this.#transcriber = null;
    }
    this.#isRecording = false;
    this.#stopTimer();
  }

  /** Capitalize, strip stray dots, end with a period; prefix a space when appending. */
  #formatSpeech(input: string, appending: boolean): string {
    const trimmed = (input || '').trim();
    if (!trimmed.length) return '';
    const formatted = (trimmed[0].toUpperCase() + trimmed.slice(1).toLowerCase()).replace(/[.]/g, ' ');
    const sentence = formatted.trim() + '.';
    return appending ? ' ' + sentence : sentence;
  }

  // ---- Theme ----

  /** Flip dark/light and persist the choice (host attr reflects it for CSS). */
  toggleTheme() {
    this.isDark = !this.isDark;
    localStorage.setItem('nova-theme', this.isDark ? 'dark' : 'light');
  }

  /** Desktop-only: expand the docked panel to full screen (or restore it). */
  toggleFullscreen() {
    this.isFullscreen = !this.isFullscreen;
    // Full screen ignores any dragged position — clear it so restoring re-docks.
    if (this.isFullscreen) { this.panelLeft = null; this.panelTop = null; }
  }

  // ---- Mode + Assistant chat ----

  async openAssistant() {
    if (!this.assistantAvailable) return;
    this.#pruneStaleAutoAttachments();
    this.mode = 'assistant';
    this.isOpen = true;
    // Read the index when the surface opens, not at construction: this component is created during
    // app boot, before the local database is ready, and a read that early comes back empty — which
    // would hide the history button from a rep who does have conversations.
    void this.refreshConversationHistory();
    this.isLoadingChat = true;
    try {
      this.chatMessages = await this.#assistant.open();
    } catch {
      this.chatMessages = [];
    } finally {
      this.isLoadingChat = false;
    }
    void this.#loadWelcome();
  }

  /**
   * Fetch the welcome card for an empty thread. No-op once the thread has messages,
   * and skipped offline — the greeting needs the agent. Cached in the feature, so
   * reopening the panel costs nothing.
   */
  async #loadWelcome(): Promise<void> {
    if (this.chatMessages.length || this.#deviceService.isOffline) return;
    this.isWelcomeLoading = true;
    try {
      const card = await this.#assistant.welcome();
      // A message may have landed while the turn was in flight; don't replace a live
      // thread's empty state retroactively.
      if (!this.chatMessages.length) this.welcomeCard = card;
    } finally {
      this.isWelcomeLoading = false;
    }
  }

  /**
   * The rep is nearing the end of the starter prompts. Grow the pool in the background so
   * the next tap has new ones; the prompts on screen stay put while it runs.
   */
  onTopUpWelcome(): void {
    void this.#assistant.topUpWelcomeActions();
  }

  /** True while more starter prompts are being fetched — drives the placeholder row. */
  get isToppingUpWelcome(): boolean {
    return this.#assistant.isToppingUpWelcome;
  }

  /** Discard the current assistant thread and start a fresh conversation. */
  async startNewAssistantChat() {
    if (this.isSendingChat) return;
    this.chatMessages = await this.#assistant.startNew().catch(() => []);
    void this.#loadWelcome();
  }

  setMode(mode: 'assistant' | 'meeting' | 'record') {
    if (mode === 'meeting' && !this.meetingAvailable) return;
    if (mode === 'record' && !this.recordAvailable) return;
    if (mode === 'assistant' && !this.assistantAvailable) return;
    if (mode === 'assistant') this.#pruneStaleAutoAttachments();
    this.mode = mode;
    if (mode === 'assistant') void this.refreshConversationHistory();
    if (mode === 'assistant' && !this.chatMessages.length) {
      this.#assistant.open()
        .then(m => (this.chatMessages = m))
        .catch(() => undefined)
        .finally(() => void this.#loadWelcome());
    }
  }

  /**
   * Resolve-on-open: open the Meeting overlay, then ask the feature for an
   * existing conversation. If a prior analysis is restored, land directly in
   * 'analysis'; otherwise fall back to the 'pre' start panel. A failure to
   * resolve also falls back to 'pre' (don't strand the user on a spinner).
   */
  async openMeeting() {
    if (!this.meetingAvailable) return;
    this.mode = 'meeting';
    this.isOpen = true;

    // Mid-flight for the active meeting: never re-resolve. Re-resolving would
    // close the open conversation and reset the view to 'pre', dropping the live
    // transcript even though recording keeps running in the background. This is
    // the path hit when switching Assistant → Meeting via the beacon menu while
    // transcribing — restore the in-progress view instead.
    if (this.#isRecording) { this.meetingFlowState = 'live'; return; }
    if (this.isProcessing) { this.meetingFlowState = 'processing'; return; }

    this.meetingFlowState = 'resolving';
    this.meetingStartError = '';
    try {
      // Re-resolve + re-resume on every open so the live conversation handle is
      // re-established (enables follow-up) even after a prior close. Cheap: a
      // local-cache hit returns immediately and the resume is just a handle.
      const restored = await this.#meetingSummary.startConversation(this.activity);
      this.analysisData = restored ?? this.analysisData;
      this.meetingFlowState = this.analysisData ? 'analysis' : 'pre';
    } catch {
      this.meetingFlowState = this.analysisData ? 'analysis' : 'pre';
    }
  }

  /**
   * Open the Record summary overlay. The scope effect has already attempted a
   * cached restore on plug, so this is a cheap state flip to 'analysis' (when a
   * summary exists) or 'pre' (when we need the rep to tap "Generate").
   */
  async openRecord() {
    if (!this.recordAvailable) return;
    this.mode = 'record';
    this.isOpen = true;
    this.recordFlowState = this.recordSummary ? 'analysis' : 'pre';
  }

  /**
   * "Generate" / "Re-generate" insights: triggers the per-kind Foundry prompt agent; the
   * transport assembles grounding (RAG over offline data) from the agent config + record.
   * Previous conversation is discarded.
   */
  async onAnalyzeRecord() {
    const r = this.record;
    if (!r) return;
    if (this.recordFlowState === 'processing') return;

    this.recordFlowState = 'processing';
    try {
      // Insights surface → the transport assembles grounding from the insights agent-config row
      // (category 2/3/4); the AI Summary card's row for the same kind is independent.
      const reply = await this.#recordInsights.analyze(r);
      this.recordSummary = reply.card;
      this.recordSummaryResponseId = reply.responseId ?? null;
      this.recordSummaryUsage = reply.usage ?? null;
      this.recordLastAnalyzedAt = new Date();
      this.recordFlowState = 'analysis';
    } catch (e) {
      console.error('Record summary analyze error:', e);
      this.recordFlowState = this.recordSummary ? 'analysis' : 'pre';
      await this.#showToast(this.#translate.instant('NOVA_INSIGHTS_FAILED'), 'danger', 3000);
    }
  }

  /**
   * From the analysis view: discard the current summary and return to the 'pre'
   * start panel so the rep can capture a fresh recording. Guarded by a confirm.
   */
  async startNewRecording() {
    const alert = await this.#alertCtrl.create({
      header: this.#translate.instant('NOVA_DISCARD_SUMMARY_TITLE'),
      message: this.#translate.instant('NOVA_DISCARD_SUMMARY_MESSAGE'),
      buttons: [
        {
          text: this.#translate.instant('NOVA_KEEP_SUMMARY'),
          role: 'cancel',
        },
        {
          text: this.#translate.instant('NOVA_DISCARD_AND_RETRANSCRIBE'),
          role: 'destructive',
          handler: () => {
            // Reset to the start screen. The existing conversation is discarded
            // and the cache overwritten when the next transcription is analyzed.
            this.#meetingSummary.endConversation();
            this.analysisData = null;
            this.capturedTranscript = '';
            this.meetingFlowState = 'pre';
          },
        },
      ],
    });
    await alert.present();
  }

  /**
   * Re-run analysis on the existing transcript (no new recording). Pulls the
   * transcript from the stored conversation, then analyzes it — which opens a new
   * conversation/session and discards the previous one.
   */
  async onReanalyze() {
    if (!this.activity || this.isProcessing) return;
    const transcript = await this.#meetingSummary.getTranscript();
    if (!transcript) {
      await this.#showToast(this.#translate.instant('NOVA_NOTHING_TO_REANALYZE'), 'warning', 3000);
      return;
    }
    this.meetingFlowState = 'processing';
    this.isProcessing = true;
    try {
      const summary = await this.#meetingSummary.analyze(transcript);
      if (summary) {
        this.analysisData = summary;
        this.#captureMeetingTurn();
        this.capturedTranscript = transcript;
        this.meetingFlowState = 'analysis';
        this.goToPage(1);
      } else {
        this.meetingFlowState = 'analysis';
        await this.#showToast(this.#translate.instant('NOVA_REANALYZE_FAILED'), 'danger', 3000);
      }
    } catch (error) {
      console.error('Nova reanalyze error:', error);
      this.meetingFlowState = 'analysis';
      await this.#showToast(this.#translate.instant('NOVA_REANALYZE_FAILED'), 'danger', 3000);
    } finally {
      this.isProcessing = false;
    }
  }

  /** Bubbled up from the chat-thread <nova-mic-input> child. */
  onVoiceError(message: string) {
    if (!message) return;
    void this.#showToast(message, 'warning', 3000);
  }

  /**
   * Sends `text` as the rep's next assistant turn, from the composer or from a prompt action.
   * While the device is offline the thread is left unchanged: the offline notice is shown and the
   * attachment chips stay, so the rep can send again once connected. When the device goes offline
   * while the reply is loading, every request of the turn is aborted. A failed turn adds an error
   * reply whose Retry action sends the same text again; its text follows {@link #failureMessageKey}.
   */
  async onChatSend(text: string) {
    if (!text?.trim()) return;
    // Ignore sends while the thread is still resolving (openFor / resume in
    // flight) — sending now would create an orphaned conversation before the
    // handle lands.
    if (this.isSendingChat || this.isLoadingChat) return;
    if (this.#deviceService.isOffline) {
      this.#notifications.notify(this.#translate.instant('YOUR_DEVICE_IS_OFFLINE'), 'NovaVoiceComponent', 'top', ToastStyle.DANGER);
      return;
    }
    const atts = this.assistantAttachments;
    this.assistantAttachments = [];
    this.#autoAttachedKeys.clear();
    this.chatMessages = [...this.chatMessages, { role: 'user', text, attachments: atts.length ? [...atts] : null }];
    this.isSendingChat = true;
    const offline = new AbortController();
    const offlineWatch = this.#deviceService.isOfflineObservable
      .pipe(filter(isOffline => isOffline), take(1))
      .subscribe(() => offline.abort());
    try {
      const reply = await this.#assistant.send(text, atts, offline.signal);
      this.chatMessages = [...this.chatMessages, {
        role: 'assistant',
        text: reply.text,
        card: reply.card,
        responseId: reply.responseId,
        usage: reply.usage,
      }];
    } catch (e) {
      console.error('Assistant send error:', e);
      const message = this.#translate.instant(await this.#failureMessageKey(e, offline.signal));
      const retry: CardAction = { label: this.#translate.instant('RETRY'), kind: 'prompt', value: text };
      this.chatMessages = [...this.chatMessages, { role: 'assistant', text: message, card: textCard(message, [retry]) }];
    } finally {
      offlineWatch.unsubscribe();
      this.isSendingChat = false;
      // The first message is what puts a conversation in the index, so the history button can
      // become available as a side effect of sending.
      void this.refreshConversationHistory();
    }
  }

  /**
   * The message key of the error reply for a failed assistant turn, chosen by the cause of the failure:
   * the offline text when the device went offline during the turn (`offline` is aborted), or when the
   * request got no HTTP response and a connection check also fails; the failure text for an HTTP error,
   * and for a timeout or any other failure while the device is connected.
   */
  async #failureMessageKey(error: unknown, offline: AbortSignal): Promise<string> {
    if (offline.aborted) return 'AI_ASSISTANT_OFFLINE_RETRY';
    const { status } = (error ?? {}) as { status?: unknown };
    if (typeof status === 'number') return 'AI_ASSISTANT_RESPONSE_FAILED';
    return await this.#deviceService.checkNetworkConnectivity() ? 'AI_ASSISTANT_RESPONSE_FAILED' : 'AI_ASSISTANT_OFFLINE_RETRY';
  }

  // ---- Nova Discuss entry points (meeting + record) ----

  /**
   * Route to the tool-aware assistant pre-seeded with the active meeting context.
   */
  async discussMeeting() {
    const m = this.activity;
    if (!m) return;
    const attachment: AssistantAttachment = { kind: 'meeting', id: m.ID, label: m.subject || this.hcpName || this.#translate.instant('MEETING') };
    const origin: ConversationOrigin = { kind: 'meeting', id: m.ID };
    this.mode = 'assistant';
    this.isOpen = true;
    this.isLoadingChat = true;
    try {
      this.chatMessages = await this.#assistant.openFor(origin, attachment);
      // Show the pre-attached context as a chip above the composer ONLY for a
      // fresh draft (empty thread). A resumed conversation already carries its
      // attachment chips inside the restored message bubbles.
      this.#setAutoAttachment(this.chatMessages.length ? null : attachment);
    } finally {
      this.isLoadingChat = false;
    }
  }

  /**
   * Route to the tool-aware assistant pre-seeded with the active record context.
   */
  async discussRecord() {
    const r = this.record;
    if (!r) return;
    const origin: ConversationOrigin = { kind: r.kind, id: r.id };
    const attachment: AssistantAttachment = { kind: r.kind, id: r.id, label: r.label };
    this.mode = 'assistant';
    this.isOpen = true;
    this.isLoadingChat = true;
    try {
      this.chatMessages = await this.#assistant.openFor(origin, attachment);
      this.#setAutoAttachment(this.chatMessages.length ? null : attachment);
    } finally {
      this.isLoadingChat = false;
    }
  }

  // ---- Attach picker ----

  /**
   * The in-scope "attach this" shortcuts for the picker. A meeting offers two —
   * the meeting (→ analysis) and its transcript; a contact/account offers one.
   * Spotlight has no single attachable id, so none.
   */
  // Memoized so the getter returns a STABLE array reference across change-detection
  // cycles. Without this it allocated new objects every CD, which (with the
  // picker's *ngFor) destroyed+recreated rows mid-tap and broke attach toggling.
  #ctxCacheKey = '';
  #ctxCacheVal: AssistantAttachment[] = [];
  get contextAttachments(): AssistantAttachment[] {
    // Build a row for EVERY in-scope context (most-recent first), not just the
    // topmost — a meeting yields two rows (analysis + transcript), each record one.
    const contexts = this.#context.activeContexts();
    const key = contexts.map(c =>
      c.kind === 'meeting'
        ? `m:${c.activity.ID}:${c.activity.subject ?? ''}:${this.hcpName}`
        : `r:${c.record.kind}:${c.record.id}:${c.record.label}`,
    ).join('|') || 'none';
    if (key === this.#ctxCacheKey) return this.#ctxCacheVal;
    this.#ctxCacheKey = key;
    const out: AssistantAttachment[] = [];
    for (const c of contexts) {
      if (c.kind === 'meeting') {
        const label = c.activity.subject || this.hcpName || this.#translate.instant('MEETING');
        out.push({ kind: 'meeting', id: c.activity.ID, label });
        out.push({ kind: 'transcript', id: c.activity.ID, label });
      } else {
        const r = c.record;
        out.push(r.kind === 'spotlight'
          ? { kind: 'spotlight', id: r.id, label: r.label }
          : { kind: r.kind as 'contact' | 'account', id: r.id, label: r.label });
      }
    }
    this.#ctxCacheVal = out;
    return this.#ctxCacheVal;
  }

  /** Chip keys (`kind:id`) seeded automatically by a Discuss entry, not by the rep. */
  #autoAttachedKeys = new Set<string>();

  #attachmentKey(a: AssistantAttachment): string { return `${a.kind}:${a.id}`; }

  /** Seed the composer chip for a Discuss entry and mark it as auto-attached. */
  #setAutoAttachment(attachment: AssistantAttachment | null) {
    this.#autoAttachedKeys.clear();
    this.assistantAttachments = attachment ? [attachment] : [];
    if (attachment) this.#autoAttachedKeys.add(this.#attachmentKey(attachment));
  }

  /**
   * Auto-attached context follows the surface it came from: once that meeting or
   * record is no longer an active context (the rep navigated away), its chip is
   * removed. Chips the rep attached explicitly are kept — only implicit ones expire.
   */
  #pruneStaleAutoAttachments() {
    if (!this.#autoAttachedKeys.size) return;
    const active = new Set<string>();
    for (const c of this.#context.activeContexts()) {
      if (c.kind === 'meeting') {
        active.add(`meeting:${c.activity.ID}`);
        active.add(`transcript:${c.activity.ID}`);
      } else {
        active.add(`${c.record.kind}:${c.record.id}`);
      }
    }
    const stale = [...this.#autoAttachedKeys].filter(k => !active.has(k));
    if (!stale.length) return;
    for (const k of stale) this.#autoAttachedKeys.delete(k);
    this.assistantAttachments = this.assistantAttachments.filter(
      a => !stale.includes(this.#attachmentKey(a)),
    );
  }

  openPicker() { this.isPickerOpen = true; }

  /**
   * Toggle an attachment from the picker: add if absent, remove if already on.
   * Stays open so the rep can attach several and see the checkmark state; a
   * search-result pick (one-off) closes via onAttachAndClose below.
   */
  onAttach(a: AssistantAttachment) {
    // Any picker interaction makes the chip the rep's own choice — auto no longer applies.
    this.#autoAttachedKeys.delete(this.#attachmentKey(a));
    const exists = this.assistantAttachments.some(x => x.id === a.id && x.kind === a.kind);
    this.assistantAttachments = exists
      ? this.assistantAttachments.filter(x => !(x.id === a.id && x.kind === a.kind))
      : [...this.assistantAttachments, a];
  }

  /** Search-result pick: add (if absent) then close the picker — a one-off action. */
  onAttachAndClose(a: AssistantAttachment) {
    this.#autoAttachedKeys.delete(this.#attachmentKey(a));
    if (!this.assistantAttachments.some(x => x.id === a.id && x.kind === a.kind)) {
      this.assistantAttachments = [...this.assistantAttachments, a];
    }
    this.isPickerOpen = false;
  }

  onRemoveAttachment(a: AssistantAttachment) {
    this.#autoAttachedKeys.delete(this.#attachmentKey(a));
    this.assistantAttachments = this.assistantAttachments.filter(x => !(x.id === a.id && x.kind === a.kind));
  }

  // ---- Conversation history ----

  openHistory() { this.isHistoryOpen = true; }

  /** Re-read whether the rep has any conversation, from the history list: the local index merged with the
   *  conversations Foundry keeps for the rep. */
  async refreshConversationHistory(): Promise<void> {
    try {
      this.hasConversationHistory = (await this.#assistant.listConversations()).length > 0;
    } catch {
      this.hasConversationHistory = false;
    }
  }

  async onResumeConversation(entry: NovaConversationEntry) {
    this.isHistoryOpen = false;
    this.#pruneStaleAutoAttachments();
    this.mode = 'assistant';
    this.isLoadingChat = true;
    try {
      this.chatMessages = await this.#assistant.resume(entry.id, entry.origin, entry.title);
    } finally {
      this.isLoadingChat = false;
    }
  }

  async newConversation() {
    this.isHistoryOpen = false;
    this.chatMessages = await this.#assistant.startNew().catch(() => []);
    await this.refreshConversationHistory();
    void this.#loadWelcome();
  }

  /** Delete a conversation from the history sheet (server-side + local index). If
   *  it was the one currently open, collapse the thread to a fresh chat. */
  async onDeleteConversation(entry: NovaConversationEntry) {
    const wasOpen = this.mode === 'assistant' && this.#assistant.currentConversationId === entry.id;
    await this.#assistant.deleteConversation(entry.id).catch(() => undefined);
    if (wasOpen) this.chatMessages = [];
    await this.refreshConversationHistory();
  }

  /**
   * Tap on an entity card, or on an action button that opens a record, inside an
   * assistant card → open that record. Reuses
   * the same client-tool navigation as the agent's view_contact/view_account,
   * then closes the panel so the rep lands on the record.
   */
  async onOpenEntity(e: { kind: 'contact' | 'account'; id: string }): Promise<void> {
    try {
      if (e.kind === 'contact') await this.#navTools.viewContact(e.id);
      else await this.#navTools.viewAccount(e.id);
      await this.close();
    } catch (err) {
      console.error('onOpenEntity failed:', err);
    }
  }

  /**
   * A card-level / per-insight action chip the `<nova-card>` shell re-emitted.
   * `kind:'tool'` is dispatched inside the shell (it never reaches here); a
   * `kind:'prompt'` chip sends its `value` as the next user turn.
   */
  onCardAction(action: CardAction): void {
    if (action.kind === 'prompt') void this.onChatSend(action.value);
  }

  /**
   * A thumbs vote from a `<nova-card>` footer — assistant chat and Nova Insights both re-emit
   * through this one handler. Fire-and-forget: the vote is best-effort telemetry, never blocking
   * on the panel.
   */
  /**
   * Lifts the analysis turn's response id + token usage onto the component so the meeting
   * overview card can show the feedback footer. A restored analysis has no turn, so the
   * footer stays hidden rather than offering a vote that could not be attached to anything.
   */
  #captureMeetingTurn(): void {
    const turn = this.#meetingSummary.lastTurn;
    this.meetingSummaryResponseId = turn?.responseId ?? null;
    this.meetingSummaryUsage = turn?.usage ?? null;
  }

  onCardFeedback(e: NovaFeedbackEvent): void {
    this.#feedback.submitFeedback(e.responseId, e.up, e.comment);
  }

  /**
   * `pre` → `live`. Starts transcription; on success collapses the panel so the
   * pulsating recording FAB takes over. On failure (offline / no speech config /
   * permission denied) keeps the panel open and shows an inline message.
   */
  async startMeeting() {
    this.meetingStartError = '';

    if (this.#deviceService.isOffline) {
      this.meetingStartError = this.#translate.instant('NOVA_TRANSCRIPTION_OFFLINE');
      return;
    }
    if (!this.#authService.speechConfig?.speechSubscriptionKey) {
      this.meetingStartError = this.#translate.instant('NOVA_TRANSCRIPTION_NOT_CONFIGURED');
      return;
    }

    await this.startTranscription();

    if (this.#isRecording) {
      this.meetingFlowState = 'live';
      this.isOpen = false; // collapse → recording FAB (existing visuals + timer)
    } else if (!this.meetingStartError) {
      this.meetingStartError = this.#translate.instant('NOVA_MICROPHONE_PERMISSION');
    }
  }

  /**
   * `live` → `processing` → `analysis`. Stops transcription and runs analysis.
   * Empty transcript resets to `pre` with a toast; the panel stays open while
   * processing so the in-flight "analyzing" state is visible.
   */
  async stopMeeting() {
    this.mode = 'meeting';
    this.isOpen = true;
    this.meetingFlowState = 'processing';

    const transcript = await this.stopTranscription();
    if (transcript) {
      await this.showAnalysis(); // sets meetingFlowState='analysis' on success, 'pre' on failure
    } else {
      this.meetingFlowState = 'pre';
      this.#showToast(this.#translate.instant('NOVA_NOTHING_CAPTURED'), 'warning', 3000);
    }
  }

  /** Opens the Meeting overlay; analyzes only a *new* transcript. Reopening an
   *  already-analyzed meeting just shows it again — it never re-sends to Foundry. */
  async showAnalysis() {
    // Nothing to show: no new transcript and no prior analysis — don't pop an empty panel.
    if (!this.capturedTranscript && !this.analysisData) return;
    this.mode = 'meeting';
    this.isOpen = true;
    // Already analyzed → reopen the existing result. Guarding on analysisData
    // (not transcript emptiness) prevents a duplicate analyze when the panel is
    // collapsed and reopened while capturedTranscript is still in memory.
    if (this.analysisData) {
      this.meetingFlowState = 'analysis';
      return;
    }
    await this.#analyze(this.capturedTranscript);
    if (this.analysisData) this.meetingFlowState = 'analysis';
  }

  /**
   * Short context block prepended to the transcript so the agent knows the
   * subject, who the meeting was with, and when — which also helps it map the
   * diarized Guest-N speakers to roles (one of them is the named HCP).
   */
  #meetingContextPreamble(): string {
    const activity = this.activity;
    if (!activity) return '';
    const lines: string[] = [];
    if (activity.subject) lines.push(`Subject: ${activity.subject}`);
    if (this.hcpName) lines.push(`Meeting with: ${this.hcpName}`);
    const start = activity.scheduledStart;
    if (start instanceof Date && !isNaN(start.getTime())) {
      lines.push(`Date: ${start.toLocaleDateString()} ${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`);
    }
    if (!lines.length) return '';
    return `Meeting context:\n${lines.join('\n')}\n\nConversation transcript:\n`;
  }

  async #analyze(transcript: string) {
    this.isProcessing = true;
    try {
      // Discards the old conversation, creates a fresh one, analyzes, persists the
      // new id + caches the result (meeting was set at launch by startConversation).
      // Prepend meeting context so the agent has subject/HCP/date for grounding.
      const summary = await this.#meetingSummary.analyze(this.#meetingContextPreamble() + transcript);
      if (summary) {
        this.analysisData = summary;
        this.#captureMeetingTurn();
        this.goToPage(1);
      } else {
        // Agent returned a falsy summary without throwing — reset so the spinner
        // doesn't hang indefinitely in 'processing'.
        this.meetingFlowState = 'pre';
        await this.#showToast(this.#translate.instant('NOVA_ANALYZE_FAILED'), 'danger', 3000);
      }
    } catch (error) {
      console.error('Nova Voice API error:', error);
      // Analysis failed — return to the start state rather than an empty pages view.
      this.meetingFlowState = 'pre';
      await this.#showToast(this.#translate.instant('NOVA_ANALYZE_FAILED'), 'danger', 3000);
    } finally {
      this.isProcessing = false;
    }
  }

  async onApplySelections(selections: ApplySelections) {
    if (!this.activity) return;

    try {
      const applied = this.#meetingDataService.applyAiKeyMessages(this.activity, selections.keyMessages);
      if (applied) {
        await this.#meetingDataService.updateMeetingProductKeyMessages(this.activity);
        this.#events.publish('initActivityKeyMessages');
      }
      const noteSaved = await this.#saveAnalysisNote(selections);

      await this.#showToast(
        this.#translate.instant(noteSaved
          ? (applied ? 'NOVA_KEY_MESSAGES_APPLIED_NOTE_SAVED' : 'NOVA_ANALYSIS_NOTE_SAVED')
          : (applied ? 'NOVA_KEY_MESSAGES_APPLIED' : 'NOVA_NO_MATCHING_KEY_MESSAGES')),
        applied || noteSaved ? 'success' : 'warning',
        2000,
      );
    } catch (error) {
      console.error('Error applying selections:', error);
    }
  }

  /**
   * Persist the applied analysis as a meeting note (same offline-first flow as the
   * meeting page's notes section: IONote on the activity + offline DB + online push).
   */
  async #saveAnalysisNote(selections: ApplySelections): Promise<boolean> {
    const activity = this.activity;
    const analysis = this.analysisData;
    if (!activity || !analysis) return false;
    const notetext = this.#composeAnalysisNote(analysis, selections);
    if (!notetext) return false;

    const now = new Date().getTime().toString();
    const tempNote = {
      annotationid: 'offlinmeetingnote' + now,
      activityid: activity.ID,
      createdon: now,
      notetext,
      ownerName: this.#authService.user.displayName,
      ownerid: this.#authService.user.systemUserID,
      deleted: false,
      pendingPushForDynamics: true,
      isdocument: false,
      hasDocument: false,
    };
    const note = new IONote(tempNote);
    activity.meetingNotes = activity.meetingNotes ?? [];
    activity.meetingNotes.push(note);
    // Keep the activity-service copies in step so the meeting page shows the note.
    const stored = this.#activityService.getActivityByID(activity.ID);
    if (stored) stored.meetingNotes = activity.meetingNotes;
    const displayed = this.#activityService.getDisplayctivityByID(activity.ID);
    if (displayed) displayed.meetingNotes = activity.meetingNotes;

    await this.#activityService.updateMeetingNoteInOfflineDB(tempNote);
    if (!this.#deviceService.isOffline) {
      await this.#meetingDataService.uploadMeetingNotesOnline()
        .then(res => {
          if (res && Array.isArray(res) && res.length > 0 && res[0].noteid) {
            note.noteId = res[0].noteid;
          }
        })
        .catch(e => console.error('Analysis note online push failed (kept offline):', e));
    } else if (activity.ID.includes('offline')) {
      this.#activityService.upsertMeetingsOfflineData(activity);
    } else {
      await this.#disk.updateOrInsertActivityToActivityDetailRawDocument(activity, true);
    }
    return true;
  }

  /**
   * Meeting-note rendering of the analysis: plain text, scannable sections, only the
   * parts the rep applied. Empty analysis fields are skipped entirely.
   */
  #composeAnalysisNote(analysis: MeetingSummaryResult, selections: ApplySelections): string {
    const lines: string[] = [];
    lines.push(`Nova Call Analysis — ${analysis.summary_title?.trim() || 'Meeting'}`);
    if (analysis.summary?.trim()) {
      lines.push('', analysis.summary.trim());
    }
    const s = analysis.sentiment_analysis;
    if (s?.overall_sentiment) {
      const pct = Math.round((s.confidence ?? 0) * 100);
      lines.push('', `Sentiment: ${s.overall_sentiment}${pct ? ` (${pct}% confidence)` : ''}`);
    }
    if (selections.keyMessages?.length) {
      lines.push('', 'Key messages (applied):');
      for (const km of selections.keyMessages) lines.push(`- ${km.key_message}`);
    }
    if (selections.followUpItems?.length) {
      lines.push('', 'Follow-ups:');
      for (const f of selections.followUpItems) {
        lines.push(`- [${f.priority}] ${f.item}${f.due_date ? ` (due ${f.due_date})` : ''}`);
      }
    }
    return lines.join('\n');
  }

  goToPage(index: number) {
    this.currentPage = index;
    if (this.swiperRef?.nativeElement?.swiper) {
      this.swiperRef.nativeElement.swiper.slideTo(index);
    }
  }

  onSlideChange() {
    if (this.swiperRef?.nativeElement?.swiper) {
      this.currentPage = this.swiperRef.nativeElement.swiper.activeIndex;
    }
  }

  async close() {
    // Play the exit animation (panel shrinks back to the FAB corner + backdrop
    // fade) before unmounting, unless the user prefers reduced motion.
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (!reduce && this.isOpen) {
      this.isClosing = true;
      await new Promise(r => setTimeout(r, 180));
      this.isClosing = false;
    }
    this.isOpen = false;
    // Re-dock next time it opens.
    this.panelLeft = null;
    this.panelTop = null;
    // Don't leave a sheet open to flash on the next panel open.
    this.isPickerOpen = false;
    this.isHistoryOpen = false;
    this.isContextMenuOpen = false;
    this.isViewMenuOpen = false;
    this.isMoreMenuOpen = false;
    // Keep an in-flight meeting conversation alive — the FAB still represents it
    // in the background. Only tear it down when nothing is recording/processing.
    if (!this.#isRecording && !this.isProcessing) {
      await this.#meetingSummary.endConversation();
    }
    // Record-mode handle: release when not actively analyzing. The cached summary
    // + conv id stay in PouchDB for the next reopen.
    if (this.recordFlowState !== 'processing') {
      await this.#recordInsights.endConversation();
    }
  }

  // ---- Floating action button (self-contained Nova trigger) ----

  /** Show the FAB when the overlay is closed and any Nova surface is available. */
  get showFab(): boolean {
    if (this.isOpen) return false;
    return this.assistantAvailable || this.meetingAvailable || this.recordAvailable;
  }

  get isRecording(): boolean {
    return this.#isRecording;
  }

  /** Running transcript text for the live view (updates as recognition lands). */
  get liveTranscript(): string {
    return this.#transcript;
  }

  get fabState(): 'idle' | 'recording' | 'processing' | 'ready' {
    if (this.isProcessing || this.recordFlowState === 'processing') return 'processing';
    if (this.#isRecording) return 'recording';
    if (this.analysisData || this.recordSummary) return 'ready';
    return 'idle';
  }

  /**
   * Tap behaviour (recording is never stopped on tap — only re-expanded):
   *  - recording           → re-expand to the Meeting live view.
   *  - ready (analysisData) → reopen analysis.
   *  - meeting available    → open Meeting in the `pre` (start) state.
   *  - otherwise            → open Assistant.
   * Suppressed if the tap was a drag.
   */
  onFabTap() {
    if (this.isRecording) {
      this.mode = 'meeting';
      this.meetingFlowState = 'live';
      this.isOpen = true;
      return;
    }
    if (this.fabState === 'ready') {
      // Prefer record summary when meeting has no result yet — the bubble
      // represents whichever surface is loaded.
      if (this.recordSummary && !this.analysisData) { void this.openRecord(); return; }
      this.showAnalysis();
      return;
    }
    if (this.meetingAvailable) { void this.openMeeting(); return; }
    if (this.recordAvailable) { void this.openRecord(); return; }
    if (this.assistantAvailable) { this.openAssistant(); }
  }

  onFabPointerDown(event: PointerEvent) {
    this.#dragging = true;
    this.#dragMoved = false;
    this.#dragStartY = event.clientY;
    this.#dragStartTop = this.fabTop;
    this.#dragStartX = event.clientX;
    this.#dragStartLeft = this.fabLeft;
    this.#dragInsets = readSafeAreaInsets();
  }

  /** Keeps the floating button at the same distance from the right edge of the safe area when the viewport resizes or rotates. */
  @HostListener('window:resize')
  onWindowResize() {
    const insets = readSafeAreaInsets();
    this.#placeFab(
      this.fabTop,
      (window.innerWidth || 400) - insets.right - this.#fabRightOffset,
      insets,
    );
  }

  /** Re-places the floating button after a rotation, including a flip between the two landscape directions, which keeps the viewport size but moves the insets. */
  @HostListener('window:orientationchange')
  onOrientationChange() {
    requestAnimationFrame(() =>
      requestAnimationFrame(() => this.onWindowResize()),
    );
  }

  /**
   * Moves the floating button to the given position, clamped so it stays below the header and clear of
   * the display cutout, the system bars and the home indicator, and records its distance from the right edge of the safe area.
   *
   * @param top Requested distance from the top of the viewport, in CSS pixels.
   * @param left Requested distance from the left of the viewport, in CSS pixels.
   * @param insets Safe-area insets of the viewport.
   */
  #placeFab(top: number, left: number, insets: SafeAreaInsets): void {
    const width = window.innerWidth || 400;
    const height = window.innerHeight || 800;
    this.fabTop = Math.max(
      insets.top + 72,
      Math.min(height - insets.bottom - 80, top),
    );
    this.fabLeft = Math.max(
      insets.left + 8,
      Math.min(width - insets.right - 64, left),
    );
    this.#fabRightOffset = width - insets.right - this.fabLeft;
  }

  /**
   * Begin dragging the docked panel by its header (desktop, non-fullscreen).
   * Presses on header buttons / the beacon menu are ignored so they still work.
   */
  onHeaderPointerDown(event: PointerEvent) {
    if (!this.isDesktop || this.isFullscreen) return;
    const target = event.target as HTMLElement;
    if (target.closest('button') || target.closest('.nova-ctx-menu')) return;
    const container = (event.currentTarget as HTMLElement).closest('.nova-voice-container') as HTMLElement | null;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    this.panelLeft = this.#panelStartLeft = rect.left;
    this.panelTop = this.#panelStartTop = rect.top;
    this.#panelDragStartX = event.clientX;
    this.#panelDragStartY = event.clientY;
    this.#panelDragging = true;
  }

  @HostListener('document:pointermove', ['$event'])
  onPointerMove(event: PointerEvent) {
    if (this.#panelDragging) {
      const w = window.innerWidth || 1200;
      const h = window.innerHeight || 800;
      const left = this.#panelStartLeft + (event.clientX - this.#panelDragStartX);
      const top = this.#panelStartTop + (event.clientY - this.#panelDragStartY);
      this.panelLeft = Math.max(0, Math.min(w - 80, left));   // keep part on-screen
      this.panelTop = Math.max(0, Math.min(h - 56, top));
      return;
    }
    if (!this.#dragging) return;
    const dy = event.clientY - this.#dragStartY;
    const dx = event.clientX - this.#dragStartX;
    if (Math.abs(dy) > 4 || Math.abs(dx) > 4) this.#dragMoved = true;
    this.#placeFab(
      this.#dragStartTop + dy,
      this.#dragStartLeft + dx,
      this.#dragInsets,
    );
  }

  @HostListener('document:pointerup')
  onPointerUp() {
    if (this.#panelDragging) { this.#panelDragging = false; return; }
    if (!this.#dragging) return;
    this.#dragging = false;
    if (!this.#dragMoved) this.onFabTap();
  }

  /** Single place to surface a transient Nova toast (bottom, colored). */
  async #showToast(message: string, color: string, duration = 3000) {
    const toast = await this.#toastCtrl.create({ message, duration, position: 'bottom', color });
    await toast.present();
  }

  #startTimer() {
    this.#recordStartMs = Date.now();
    this.recordingElapsed = '00:00';
    this.#stopTimer();
    this.#recordTimer = setInterval(() => {
      const total = Math.floor((Date.now() - this.#recordStartMs) / 1000);
      const mm = String(Math.floor(total / 60)).padStart(2, '0');
      const ss = String(total % 60).padStart(2, '0');
      this.recordingElapsed = `${mm}:${ss}`;
    }, 1000);
  }

  #stopTimer() {
    if (this.#recordTimer) {
      clearInterval(this.#recordTimer);
      this.#recordTimer = null;
    }
  }
}
